import React from 'react';
import { createPortal } from 'react-dom';
import { Building2, Check, ChevronDown, LayoutGrid, Sparkles } from 'lucide-react';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { ALL_CLINICS, AUTO_CLINIC } from '../hooks/useActiveClinic';
import { useReceptionStrings } from '../lib/receptionStrings';

// «پذیرش در …» — the clinic whose day sheet is on screen.
//
// It sits with the date navigator, not with the page actions, because it
// answers the same kind of question: not "what do I want to do" but "what am I
// looking at" (DESIGN-CONCEPT §2.2, Archetype A's context widgets).
//
// Two looks, one behaviour: `tone="surface"` is the white pill on the desktop
// PageHeader, `tone="gradient"` the white-alpha one on the mobile brand header
// (DESIGN-CONCEPT-MOBILE §2.2 — white-alpha is the sanctioned way to tint
// on-gradient chrome). Nothing else differs, so the two surfaces cannot drift
// apart on which clinic they claim.
//
// With `mode` (useActiveClinic's) the menu leads with «خودکار — طبق برنامهٔ
// حضور»: the lens follows the presence schedule, and the chip wears a «خودکار»
// badge while it does. Without `mode` the chip is the old two-way choice.

const PANEL_WIDTH = 264;

function panelPosition(rect) {
  if (!rect) return null;
  const margin = 8;
  const width = Math.min(PANEL_WIDTH, window.innerWidth - margin * 2);
  // Anchored to the chip's INLINE-START edge, which is the right edge in
  // Persian and the left in English — a panel pinned to a physical side would
  // hang off the screen in one of the two languages.
  const rtl = document.documentElement.dir !== 'ltr';
  const left = rtl
    ? Math.max(margin, rect.right - width)
    : Math.min(rect.left, window.innerWidth - width - margin);
  return { left, width, top: rect.bottom + 6 };
}

const ClinicContextChip = ({
  clinics = [],
  activeId,
  isAll,
  activeClinic,
  single,
  mode,                   // 'auto' | 'all' | 'pick' | undefined (no AUTO option)
  onSelect,
  counts,                 // Map(clinicId → appointments today) | undefined
  tone = 'surface',
  testId = 'reception-clinic',
}) => {
  const { t, formatNumber } = useTranslation();
  const rs = useReceptionStrings();
  const withAuto = mode !== undefined && clinics.length > 1;
  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState(null);
  const triggerRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const onGradient = tone === 'gradient';

  const reposition = React.useCallback(() => {
    const el = triggerRef.current;
    if (el) setRect(panelPosition(el.getBoundingClientRect()));
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The panel is portaled to <body>, so it sits at the END of the tab order —
  // opening it and pressing Tab would walk the whole page first. Focus starts on
  // the clinic already chosen (§12.2: the menu opens ON its current value), and
  // the arrow keys walk the rows from there.
  React.useEffect(() => {
    if (!open || !rect) return;
    const rows = panelRef.current?.querySelectorAll('[role="menuitemradio"]');
    const at = [...(rows || [])].find((r) => r.getAttribute('aria-checked') === 'true');
    (at || rows?.[0])?.focus();
  }, [open, rect]);

  const onMenuKeyDown = (e) => {
    const rows = [...(panelRef.current?.querySelectorAll('[role="menuitemradio"]') || [])];
    if (!rows.length) return;
    const at = rows.indexOf(document.activeElement);
    const go = (i) => { e.preventDefault(); rows[Math.max(0, Math.min(rows.length - 1, i))]?.focus(); };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(rows.length - 1);
    else if (e.key === 'Tab') setOpen(false);
  };

  if (!clinics.length) return null;   // absent, never disabled (§2.1)

  const label = isAll ? t('dashboard.clinic.all') : (activeClinic?.name || t('common.labels.clinic'));
  const city = isAll ? '' : (activeClinic?.city || '');

  const pick = (id) => {
    onSelect?.(id);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const chip = (
    <button
      ref={triggerRef}
      type="button"
      data-testid={testId}
      data-clinic-id={activeId}
      aria-haspopup={single ? undefined : 'menu'}
      aria-expanded={single ? undefined : open}
      aria-label={single ? undefined : t('dashboard.clinic.pick')}
      // With one membership the chip is a statement, not a control: it carries
      // no caret, takes no focus and opens nothing. Rendering it as a dead
      // button would promise a choice that does not exist.
      disabled={single}
      onClick={single ? undefined : () => setOpen((o) => !o)}
      className="outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        height: 32, padding: single ? '0 12px' : '0 8px 0 12px',
        borderRadius: 999,
        border: `1px solid ${onGradient ? 'rgba(255,255,255,0.28)' : C.border}`,
        background: onGradient ? 'rgba(255,255,255,0.16)' : '#fff',
        color: onGradient ? '#fff' : C.textPrimary,
        fontFamily: 'var(--app-font)', fontSize: '0.78125rem', fontWeight: 700,
        cursor: single ? 'default' : 'pointer',
        maxWidth: '100%', minWidth: 0,
        opacity: 1,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          background: onGradient ? '#fff' : C.green,
          boxShadow: onGradient ? 'none' : `0 0 0 3px ${soft(C.green, 0.2)}`,
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {city && (
        <span style={{ fontWeight: 500, color: onGradient ? 'rgba(255,255,255,0.75)' : C.textSecondary, whiteSpace: 'nowrap' }}>
          · {city}
        </span>
      )}
      {withAuto && mode === 'auto' && (
        <span data-testid={`${testId}-auto`} style={{
          fontSize: '0.65625rem', fontWeight: 700, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap',
          background: onGradient ? 'rgba(255,255,255,0.22)' : soft(C.navy, 0.1),
          color: onGradient ? '#fff' : C.navy,
        }}>
          {rs('dashboard.clinic.autoBadge')}
        </span>
      )}
      {!single && (
        <ChevronDown size={14} aria-hidden style={{ color: onGradient ? 'rgba(255,255,255,0.75)' : C.textMuted, flexShrink: 0 }} />
      )}
    </button>
  );

  const rows = [
    ...(withAuto ? [{ id: AUTO_CLINIC, name: rs('dashboard.clinic.auto'), meta: rs('dashboard.clinic.autoSub'), auto: true }] : []),
    ...clinics.map((c) => ({
      id: c.id,
      name: c.name || t('common.labels.clinic'),
      meta: [c.city, counts?.has(c.id) ? t('dashboard.clinic.countToday', { count: formatNumber(counts.get(c.id)) }) : null]
        .filter(Boolean).join(' · '),
      owned: c.owned,
    })),
    { id: ALL_CLINICS, name: t('dashboard.clinic.all'), meta: t('dashboard.clinic.allSub'), all: true },
  ];

  const panel = open && rect ? createPortal(
    <div
      ref={panelRef}
      role="menu"
      data-testid={`${testId}-menu`}
      aria-label={t('dashboard.clinic.pick')}
      onKeyDown={onMenuKeyDown}
      style={{
        position: 'fixed', top: rect.top, left: rect.left, width: rect.width,
        zIndex: 3000, background: '#fff',
        border: `1px solid ${C.border}`, borderRadius: 14,
        boxShadow: '0 18px 44px rgba(15,23,42,0.18)',
        padding: 6, fontFamily: 'var(--app-font)',
        maxHeight: '60vh', overflowY: 'auto',
      }}
    >
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.04em', color: C.textMuted, padding: '6px 10px 4px' }}>
        {t('dashboard.clinic.label')}
      </div>
      {rows.map((row) => {
        // With AUTO on offer, a row is checked by the MODE, not by the id the
        // lens resolved to: following the schedule to clinic X is not the same
        // choice as picking clinic X, and the menu must not pretend it is.
        const checked = withAuto
          ? (row.auto ? mode === 'auto' : row.all ? mode === 'all' : mode === 'pick' && row.id === activeId)
          : row.id === activeId;
        return (
          <button
            key={row.id}
            type="button"
            role="menuitemradio"
            aria-checked={checked}
            data-testid={`${testId}-option-${row.id}`}
            onClick={() => pick(row.id)}
            className="outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              minHeight: 44, padding: '6px 10px', textAlign: 'start',
              border: 'none', borderRadius: 10, cursor: 'pointer',
              background: checked ? soft(C.navy, 0.08) : 'transparent',
              color: C.textPrimary, fontFamily: 'var(--app-font)',
              borderTop: row.all ? `1px solid ${C.border}` : undefined,
              marginTop: row.all ? 4 : 0,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                display: 'grid', placeItems: 'center',
                background: row.all ? C.bg : soft(row.owned ? C.teal : C.navy, 0.12),
                color: row.all ? C.textSecondary : (row.owned ? C.teal : C.navy),
              }}
            >
              {row.auto ? <Sparkles size={15} /> : row.all ? <LayoutGrid size={15} /> : <Building2 size={15} />}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.name}
              </span>
              {row.meta && (
                <span style={{ display: 'block', fontSize: '0.6875rem', color: C.textSecondary }}>{row.meta}</span>
              )}
            </span>
            {checked && <Check size={16} aria-hidden style={{ color: C.navy, flexShrink: 0 }} />}
          </button>
        );
      })}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {chip}
      {panel}
    </>
  );
};

export default ClinicContextChip;
